import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../auth/firebaseConfig';
import { useAuth } from '../auth/AuthContext';
import { createCart, getCart, addToCart, updateCartItem, removeFromCart } from '../services/shopify';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(false);

  // Remember the cart id in localStorage (guests) AND on the user doc
  // (signed in) so carts follow the account across devices and sign-outs
  const persistCartId = async (cartId) => {
    localStorage.setItem('shopify_cart_id', cartId);
    if (currentUser) {
      try {
        await setDoc(doc(db, 'users', currentUser.uid), { shopifyCartId: cartId }, { merge: true });
      } catch (e) { /* profile write is best-effort */ }
    }
  };

  const startFreshCart = async () => {
    const newCart = await createCart();
    setCart(newCart);
    await persistCartId(newCart.id);
    return newCart;
  };

  useEffect(() => {
    const initCart = async () => {
      try {
        // Candidate carts: the account's saved cart, then this browser's.
        // Validate each (Shopify carts expire ~10 days / wrong store).
        let accountCart = null;
        if (currentUser) {
          try {
            const snap = await getDoc(doc(db, 'users', currentUser.uid));
            const savedId = snap.exists() ? snap.data().shopifyCartId : null;
            if (savedId) accountCart = await getCart(savedId);
          } catch (e) { /* fall through to local cart */ }
        }
        const localId = localStorage.getItem('shopify_cart_id');
        const localCart = localId && localId !== accountCart?.id ? await getCart(localId) : null;

        // Prefer the account cart when it has items; otherwise adopt the
        // browser cart (e.g. guest filled a cart then signed in) and save
        // it to the account.
        const chosen =
          (accountCart?.lines?.edges?.length ? accountCart : null) ||
          localCart || accountCart;

        if (chosen) {
          setCart(chosen);
          await persistCartId(chosen.id);
        } else {
          await startFreshCart();
        }
      } catch (error) {
        console.error('Error initializing cart:', error);
      }
    };
    initCart();
    // Re-resolve when auth state changes (sign in/out)
  }, [currentUser?.uid]);

  const addItem = async (merchandiseId, quantity = 1) => {
    setLoading(true);
    try {
      const currentCart = cart || await startFreshCart();
      try {
        const updatedCart = await addToCart(currentCart.id, [{ merchandiseId, quantity }]);
        setCart(updatedCart);
        return updatedCart;
      } catch (e) {
        // Stale/expired cart — start a new one and retry once
        const newCart = await startFreshCart();
        const updatedCart = await addToCart(newCart.id, [{ merchandiseId, quantity }]);
        setCart(updatedCart);
        return updatedCart;
      }
    } finally {
      setLoading(false);
    }
  };

  const updateItem = async (lineId, quantity) => {
    setLoading(true);
    try {
      const updatedCart = await updateCartItem(cart.id, [{ id: lineId, quantity }]);
      setCart(updatedCart);
      return updatedCart;
    } finally {
      setLoading(false);
    }
  };

  const removeItem = async (lineId) => {
    setLoading(true);
    try {
      const updatedCart = await removeFromCart(cart.id, [lineId]);
      setCart(updatedCart);
      return updatedCart;
    } finally {
      setLoading(false);
    }
  };

  const getCartCount = () => {
    if (!cart || !cart.lines) return 0;
    return cart.lines.edges.reduce((total, item) => total + item.node.quantity, 0);
  };

  const value = {
    checkout: cart,
    loading,
    addItem,
    updateItem,
    removeItem,
    cartCount: getCartCount(),
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
